import React, { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { validateEmail } from "@repo/shopify/utils/regex";

import Button from "@atoms/Button";
import Input from "@atoms/Input";

import useCustomerQuery from "@hooks/useCustomerQuery";
import useFormErrorHandler from "@hooks/useFormErrorHandler";

interface IProps {
  onClickRegister: () => void;
  submit: ({ email, password, acceptsMarketing }: { email: string; password: string; acceptsMarketing?: boolean }) => Promise<void>;
}

interface IFormData {
  email: string;
}

export default function Reset({ onClickRegister, submit }: IProps) {
  const { t } = useTranslation();

  const { resetPasswordLoading } = useCustomerQuery();

  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setFocus,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm<IFormData>({
    defaultValues: { email: "" },
    reValidateMode: "onSubmit",
  });

  useEffect(() => {
    setFocus("email");
  }, []);

  const { onCommonError, displayError } = useFormErrorHandler({ values: getValues(), errors });

  const onSubmit: SubmitHandler<IFormData> = async (formData) => {
    setIsLoading(true);
    await submit({ email: formData.email, password: "" }).finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-title">Reset Password</span>
        <span>{t("account.reset_password_email")}</span>
      </div>
      <form className="flex flex-col gap-2.5" onSubmit={handleSubmit(onSubmit, onCommonError)}>
        <Input
          {...displayError("email")}
          {...register("email", {
            required: { value: true, message: t("error.necessary_email") },
            validate: (value) => {
              if (!validateEmail(value)) return t("login.invalid_email");
            },
            onChange: () => {
              clearErrors("email");
            },
          })}
          label="Email"
          placeholder="Email Address"
          type="email"
        />
        <div className="pc:gap-1 flex flex-col gap-2.5">
          <Button as="square" className="w-full" loading={resetPasswordLoading || isLoading} name="reset" type="submit">
            Send Reset Email
          </Button>
          <Button as="square" className="w-full" name="register" onClick={onClickRegister} variant="secondary">
            Create Account
          </Button>
        </div>
      </form>
    </>
  );
}
